'use client';

import classnames from 'classnames';
import Image from 'next/image';
import React, { useMemo, useRef } from 'react';

import { CastDto } from '@cineamo/api/models/movie/CastDto.types';
import { CrewDto } from '@cineamo/api/models/movie/CrewDto.types';

import { TMDB_IMAGE_RESOLUTION, TMDB_PERSON_URL } from '@/constants/tmdb-constants';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { TrimBy, trimTextToLength } from '@cineamo/ui/src/helpers/string-helper';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

const Avatar = loadAsset('/images/placeholders/im-avatar.png');

type CastOrCrewMember = Partial<CastDto & CrewDto> & { imageUrl?: string };

type MovieCastAndCrewImageDropDownContentProps = {
    className?: string;
    dataList?: CastOrCrewMember[];
    primaryLabel: keyof CastOrCrewMember;
    secondaryLabel: keyof CastOrCrewMember;
};

function CastAndCrewImageSlider(props: MovieCastAndCrewImageDropDownContentProps): React.JSX.Element {
    const { className, dataList, primaryLabel, secondaryLabel } = props;

    const castScrollContainerRef = useRef(null);

    const { styleFadedState } = useScrollState({
        scrollContainerRef: castScrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        }
    });

    function getContent(member: CastOrCrewMember) {
        return (
            <>
                <div className="relative w-[80px] h-auto aspect-[2/3] rounded-8 overflow-clip">
                    {(member.imageUrl || member.profilePath) && (
                        <Image
                            className="w-full h-full scale-on-hover object-cover"
                            placeholder="blur"
                            blurDataURL={member.imageUrl || TMDB_IMAGE_RESOLUTION.W92 + member.profilePath}
                            src={member.imageUrl || TMDB_IMAGE_RESOLUTION.ORIGINAL + member.profilePath}
                            title={[member[primaryLabel], member[secondaryLabel]].filter((it) => !!it).join(' | ')}
                            width={120}
                            height={180}
                            alt={`${member[primaryLabel]} | ${member[secondaryLabel]}`}
                        />
                    )}
                    {!(member.imageUrl || member.profilePath) && (
                        <Image
                            className="w-full h-full bg-darkBlue scale-on-hover object-contain object-top"
                            title={[member[primaryLabel], member[secondaryLabel]].filter((it) => !!it).join(' | ')}
                            src={Avatar}
                            alt={`${member[primaryLabel]}${
                                member?.[secondaryLabel] ? ' | ' + member[secondaryLabel] : ''
                            }`}
                            width={120}
                            height={180}
                        />
                    )}
                </div>
                <div className="w-[90px] flex flex-col mt-10 space-y-4">
                    {member?.[primaryLabel] && (
                        <span
                            className="w-full flex text-body-bold-14 overflow-hidden text-ellipsis"
                            title={String(member[primaryLabel])}>
                            {member[primaryLabel]}
                        </span>
                    )}
                    {/* E.g. castMember.character or crewMember.job */}
                    {member?.[secondaryLabel] && (
                        <span
                            className="w-full flex text-body-12 overflow-hidden text-ellipsis"
                            title={String(member[secondaryLabel])}>
                            {trimTextToLength(String(member[secondaryLabel]), 4, ' ...', TrimBy.Words)}
                        </span>
                    )}
                </div>
            </>
        );
    }

    return (
        <div className={classnames('relative w-full', className)}>
            <div
                className="flex overflow-x-auto space-x-16 lg:space-x-24 no-scrollbar"
                style={styleFaded(styleFadedState, 5)}
                ref={castScrollContainerRef}>
                {dataList?.map((member, index) => {
                    const content = useMemo(() => {
                        let content = getContent(member);
                        if (member.id) {
                            content = (
                                <a
                                    href={TMDB_PERSON_URL(member.id.toString())}
                                    target="_blank"
                                    rel="nofollow noreferrer"
                                    title={[member[primaryLabel], member[secondaryLabel]]
                                        .filter((it) => !!it)
                                        .join(' | ')}>
                                    {getContent(member)}
                                </a>
                            );
                        }
                        return content;
                    }, [member]);
                    return (
                        <div
                            className="flex flex-col items-center"
                            key={`CastMember#${member?.id || index}-${member.name}`}>
                            {content}
                        </div>
                    );
                })}
            </div>
            <BackAndForthButtonOverlay scrollContainerRef={castScrollContainerRef} />
        </div>
    );
}

export default CastAndCrewImageSlider;
